import { ref } from 'vue'

import type { GetOfferV1Response } from '@backmarket/http-api/src/api-specs-buyback/customer/getOfferV1'
import type { GetQuestionsKind } from '@backmarket/http-api/src/api-specs-buyback/customer/getQuestionsV3'
import { HttpApiError } from '@backmarket/http-api/src/utils/HttpApiError'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { sleep } from '@backmarket/utils/async/sleep'

import type { FunnelForm } from './useFunnelQuestions'

interface UseFetchOfferOptions {
  kind: GetQuestionsKind
  minDelay?: number
  onBeforeFetch?: (formPayload: FunnelForm) => void
  onSuccess?: (offer: GetOfferV1Response, formPayload: FunnelForm) => void
  onNoOffer?: (formPayload: FunnelForm) => void
  onError?: (error: HttpApiError, formPayload: FunnelForm) => void
}

// useAsyncData is not used here intentionally: the offer fetch triggers complex
// branching side effects (navigation, tracking, toasts) depending on the result.
// These callbacks don't map cleanly to useAsyncData's transform/error model,
// so isLoading is managed manually and callbacks are dispatched after the fetch.
export function useFetchOffer({
  kind,
  minDelay,
  onBeforeFetch,
  onSuccess,
  onNoOffer,
  onError,
}: UseFetchOfferOptions) {
  const $httpFetch = useHttpFetch()
  const isLoading = ref(false)

  async function fetchOffer(formPayload: FunnelForm): Promise<void> {
    isLoading.value = true
    onBeforeFetch?.(formPayload)

    let offerResult: GetOfferV1Response | null = null
    let offerError: HttpApiError | null = null
    let isNoOffer = false

    try {
      const offerPromise = $httpFetch(
        '/buyback-funnel/api/v1/funnel/:kind/offer',
        {
          query: formPayload,
          pathParams: { kind },
        },
      )

      const [offer] = await Promise.all([
        offerPromise,
        ...(minDelay ? [sleep(minDelay)] : []),
      ])

      offerResult = offer
    } catch (error) {
      const httpError = HttpApiError.fromAnyError(error as Error)

      if (httpError.status === 404) {
        isNoOffer = true
      } else {
        offerError = httpError
      }
    } finally {
      isLoading.value = false
    }

    if (offerResult) {
      onSuccess?.(offerResult, formPayload)
    } else if (isNoOffer) {
      onNoOffer?.(formPayload)
    } else if (offerError) {
      onError?.(offerError, formPayload)
    }
  }

  return { fetchOffer, isLoading }
}
